import { useEffect, useState } from "react";

// Api
import { GetSeries } from "../../Api/Series";
import { GetVideo } from "../../Api/Video";

// Models
import SeriesDTO from "../../Models/DTOs/SeriesDTO";
import VideoDTO from "../../Models/DTOs/VideoDTO";

export type FrontFeaturedContentAvailableItem =
    | {
          slot: "lead-film";
          kind: "film";
          requestedId: number;
          state: "available";
          content: VideoDTO;
      }
    | {
          slot: "supporting-series-one" | "supporting-series-two";
          kind: "series";
          requestedId: number;
          state: "available";
          content: SeriesDTO;
      };

export type FrontFeaturedContentUnavailableItem =
    | {
          slot: "lead-film";
          kind: "film";
          requestedId: number;
          state: "unavailable";
      }
    | {
          slot: "supporting-series-one" | "supporting-series-two";
          kind: "series";
          requestedId: number;
          state: "unavailable";
      };

export type FrontFeaturedContentItem =
    | FrontFeaturedContentAvailableItem
    | FrontFeaturedContentUnavailableItem;

export const isFrontFeaturedContentAvailable = (
    item: FrontFeaturedContentItem
): item is FrontFeaturedContentAvailableItem => item.state === "available";

interface UseFetchFrontFeaturedContentResult {
    items: FrontFeaturedContentItem[];
    loading: boolean;
    error: string;
}

const frontFeaturedContent = [
    { slot: "lead-film", kind: "film", id: 2190 },
    { slot: "supporting-series-one", kind: "series", id: 185 },
    { slot: "supporting-series-two", kind: "series", id: 205 },
] as const;

type CuratedFrontFeaturedContentItem =
    (typeof frontFeaturedContent)[number];

const getUnavailableItem = (
    item: CuratedFrontFeaturedContentItem
): FrontFeaturedContentUnavailableItem => {
    if (item.kind === "film") {
        return {
            slot: item.slot,
            kind: item.kind,
            requestedId: item.id,
            state: "unavailable",
        };
    }

    return {
        slot: item.slot,
        kind: item.kind,
        requestedId: item.id,
        state: "unavailable",
    };
};

const isReleasedFilm = (film: VideoDTO): boolean => {
    const releaseDate = film.Datetime?.trim();
    if (!releaseDate) return false;

    const releaseTime = new Date(releaseDate).getTime();
    return Number.isFinite(releaseTime) && releaseTime <= Date.now();
};

/**
 * Loads the homepage's deliberately curated films and series from the public
 * catalogue APIs. Each request settles independently and every curated
 * position is retained, even when an environment is temporarily missing a
 * title.
 */
export default function useFetchFrontFeaturedContent(): UseFetchFrontFeaturedContentResult {
    const [items, setItems] = useState<FrontFeaturedContentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let isCancelled = false;
        const abortController = new AbortController();

        const fetchFeaturedContent = async () => {
            setLoading(true);
            setError("");

            const results = await Promise.allSettled(
                frontFeaturedContent.map(async (curatedItem) => {
                    if (curatedItem.kind === "film") {
                        const film = await GetVideo(
                            curatedItem.id,
                            abortController
                        );

                        if (!isReleasedFilm(film)) {
                            throw new Error(
                                "The curated homepage film is not published."
                            );
                        }

                        return {
                            slot: curatedItem.slot,
                            kind: "film",
                            requestedId: curatedItem.id,
                            state: "available",
                            content: film,
                        } as const;
                    }

                    const series = await GetSeries(
                        curatedItem.id,
                        abortController
                    );
                    return {
                        slot: curatedItem.slot,
                        kind: "series",
                        requestedId: curatedItem.id,
                        state: "available",
                        content: series,
                    } as const;
                })
            );

            if (isCancelled) return;

            const settledItems = results.map<FrontFeaturedContentItem>(
                (result, index) =>
                    result.status === "fulfilled"
                        ? result.value
                        : getUnavailableItem(frontFeaturedContent[index])
            );
            const unavailableItemCount = settledItems.filter(
                (item) => !isFrontFeaturedContentAvailable(item)
            ).length;

            setItems(settledItems);
            setError(
                unavailableItemCount === settledItems.length
                    ? "Featured films and series are unavailable right now."
                    : unavailableItemCount > 0
                      ? `${unavailableItemCount} of ${settledItems.length} featured selections ${
                            unavailableItemCount === 1 ? "is" : "are"
                        } temporarily unavailable.`
                      : ""
            );
            setLoading(false);
        };

        void fetchFeaturedContent();

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, []);

    return { items, loading, error };
}
